import { useEffect, useState } from 'react'

export interface BlobUrlResult {
  url: string | null
  loading: boolean
  error: string | null
}

/**
 * Fetches `url` as a cookie-authenticated blob and exposes a
 * `URL.createObjectURL` handle. Useful for rendering protected files
 * (images, PDFs, etc.) in `<img>`/`<iframe>` tags, which can't attach
 * custom headers themselves.
 *
 * The object URL is revoked on unmount and whenever `url` changes, so a
 * single hook instance never leaks more than one URL.
 */
export function useBlobUrl(url: string | null): BlobUrlResult {
  const [objectUrl, setObjectUrl] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (url === null) {
      setObjectUrl(null)
      setLoading(false)
      setError(null)
      return
    }
    let cancelled = false
    let createdUrl: string | null = null
    setLoading(true)
    setError(null)
    setObjectUrl(null)
    void (async () => {
      try {
        const res = await fetch(url, { credentials: 'include' })
        if (!res.ok) throw new Error(`Failed to load file (${res.status})`)
        const blob = await res.blob()
        if (cancelled) return
        createdUrl = URL.createObjectURL(blob)
        setObjectUrl(createdUrl)
        setLoading(false)
      } catch (err) {
        if (cancelled) return
        setError((err as Error).message)
        setLoading(false)
      }
    })()
    return () => {
      cancelled = true
      if (createdUrl) URL.revokeObjectURL(createdUrl)
    }
  }, [url])

  return { url: objectUrl, loading, error }
}
